import React, { useCallback, useMemo } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import trans from 'translate';
import { CreateHPoolChooseTokensInner } from '../../Pages/CreateHPool/CreateHPoolChooseTokens/CreateHPoolChooseTokens';
import {
  createHPoolHandleTokenPercentageChangeAction,
  setChooseTicketsAlertAction,
  setCreateHPoolChooseTokensSearchAction,
} from '../../../redux/actions/createHPoolActions';
import {
  findTokenToAddConditional,
  createHPoolHandleTokenPercentageChange,
  createHPoolHandleTokenDelete,
  getBaseToken,
} from '../../../services/createHPoolService';
import Button from '../../Common/Button/Button';
import { EDIT_TOKEN_VIEW_STAGE } from '../../../constants/hpool';
import { getValueByMultiProps, getValueByMultiPropsLowerCased } from '../../../utils/mapper';
import configJson from '../../../config/config.json';
import { arraySort } from '../../../utils/array';
import { DeleteIcon } from '../../Common/SliderTokensList/SliderToken/SliderToken';
import { createNotificationAction } from '../../../redux/actions/uiActions';

const EditTokensView = ({
  modifiedModalTokens,
  tokensListSearchVal,
  searchingTokens,
  searchingTokensError,
  tokensSearchResults,
  chooseTokensAlert,
  setModifiedModalTokens,
  onTokenEdit,
  disabledTokens,
  setCreateHPoolChooseTokensSearchAction,
  requiredTokensList,
  setChooseTicketsAlertAction,
  tokensMap,
  mostUsedTokens,
  baseTokens,
  createNotificationAction,
  isVpool,
  addedTokens,
}) => {
  const baseTokenName = addedTokens?.[0]?.symbol?.toLocaleLowerCase?.() || configJson.baseCoin.toLocaleLowerCase();

  const baseToken = useMemo(() => getBaseToken(modifiedModalTokens, baseTokenName), [modifiedModalTokens]);
  const tokensSortedByPercentage = useMemo(
    () => arraySort([...modifiedModalTokens], 'percentage', { isAsc: false }),
    [modifiedModalTokens],
  );

  const getMultiProp = (token, key) => getValueByMultiProps(token, [`token_${key}`, key]);
  const findPrevToken = (_token) =>
    baseTokens.find((token) => getMultiProp(token, 'symbol') === getMultiProp(_token, 'symbol')) || {};

  const createHPoolAddTokenFromSearch = useCallback(
    (token) => {
      setCreateHPoolChooseTokensSearchAction('');
      const tokensList = [...modifiedModalTokens];
      if (tokensMap[baseToken.token_symbol] && tokensMap[baseToken.token_symbol].min <= baseToken.percentage - 1) {
        baseToken.percentage -= 1;
        tokensList.push({ ...token, percentage: 1 });
      } else {
        const highestPercentage = tokensSortedByPercentage?.[0];
        highestPercentage.percentage -= 1;
        tokensList.push({ ...token, percentage: 1 });
      }
      setModifiedModalTokens(tokensList);
    },
    [setModifiedModalTokens, setCreateHPoolChooseTokensSearchAction, modifiedModalTokens, tokensMap],
  );

  const createHPoolHandleTokenDeleteFunc = (token) => {
    const modifiedTokens = createHPoolHandleTokenDelete(token, modifiedModalTokens);
    setModifiedModalTokens(modifiedTokens);
  };

  const detectMaxChangeLimitReached = (token, newVal, ethLeftover) => {
    const { percentage: prevVal } = token;
    if (newVal === prevVal && !ethLeftover) {
      const errMsg = trans('hpool.edit_tokens_min_limit_error', { coin: configJson.baseCoin }, null, true);
      throw new Error(errMsg);
    }
  };

  const createHPoolHandleTokenPercentageChangeFunc = (newVal, changedToken, ethLeftover) => {
    try {
      const prevToken = findPrevToken(changedToken);
      // Delte the token if it has been added recently and slided back to 0%
      if (!prevToken.id && !+newVal) {
        createHPoolHandleTokenDeleteFunc(changedToken);
        return;
      }
      const modifiedTokens = createHPoolHandleTokenPercentageChange(changedToken, newVal, modifiedModalTokens);
      setModifiedModalTokens(modifiedTokens);
    } catch (error) {
      createNotificationAction('error', error.message, 4000);
    }
  };

  const createHPoolHandleTokenLockUnlock = (token) => {
    const copiedModifiedModalTokens = [...modifiedModalTokens];
    const changedTokenIndex = copiedModifiedModalTokens.findIndex((addedModalToken) =>
      findTokenToAddConditional(token, addedModalToken),
    );

    copiedModifiedModalTokens[changedTokenIndex].locked = !token.locked;
    setModifiedModalTokens(copiedModifiedModalTokens);
  };

  const isDisabled = useCallback(
    (token) => {
      const tokenSymbol = getValueByMultiProps(token, ['symbol', 'token_symbol']);
      const inDisabledArray = disabledTokens.includes(tokenSymbol);
      return inDisabledArray;
    },
    [disabledTokens],
  );

  return (
    <div className="edit-tokens-view-container">
      {modifiedModalTokens && (
        <CreateHPoolChooseTokensInner
          stage={EDIT_TOKEN_VIEW_STAGE}
          tokensListSearchVal={tokensListSearchVal}
          createHPoolAddTokenFromSearchAction={createHPoolAddTokenFromSearch}
          createHPoolHandleTokenPercentageChangeAction={createHPoolHandleTokenPercentageChangeFunc}
          createHPoolHandleTokenDeleteAction={createHPoolHandleTokenDeleteFunc}
          createHPoolHandleTokenLockUnlockAction={createHPoolHandleTokenLockUnlock}
          setChooseTicketsAlertAction={setChooseTicketsAlertAction}
          addedTokens={modifiedModalTokens}
          requiredTokensList={requiredTokensList}
          searchingTokens={searchingTokens}
          tokensSearchResults={tokensSearchResults}
          searchingTokensError={searchingTokensError}
          chooseTokensAlert={chooseTokensAlert}
          mostUsedTokens={mostUsedTokens}
          isVpool={isVpool}
          setCreateHPoolChooseTokensSearchAction={setCreateHPoolChooseTokensSearchAction}
          actionCol={{
            title: 'Limit / StopLoss',
            render: (token) => (
              <div className="d-flex">
                {!isDisabled(token) ? (
                  <Button
                    style={{ width: '8rem', color: 'var(--green-for-pool-texts-and-numbers)' }}
                    disabled={isDisabled(token)}
                    className="outlined-btn"
                    text="common.edit"
                    onClick={() => onTokenEdit(token)}
                  />
                ) : (
                  <DeleteIcon onClick={() => createHPoolHandleTokenDeleteFunc(token)} />
                )}
              </div>
            ),
          }}
        />
      )}
    </div>
  );
};

const mapStateToProps = ({ createHPool, general }) => ({
  tokensListSearchVal: createHPool.tokensListSearchVal,
  searchingTokens: createHPool.searchingTokens,
  addedTokens: createHPool.addedTokens,
  searchingTokensError: createHPool.searchingTokensError,
  tokensSearchResults: createHPool.tokensSearchResults,
  chooseTokensAlert: createHPool.chooseTokensAlert,
  mostUsedTokens: createHPool.mostUsedTokens,
  requiredTokensList: general.requiredTokens,
  tokensMap: general.tokens,
  isVpool: createHPool.isVpool || createHPool?.hpool?.is_vpool,
});

const mapDispatchToProps = {
  createHPoolHandleTokenPercentageChangeAction,
  setCreateHPoolChooseTokensSearchAction,
  setChooseTicketsAlertAction,
  createNotificationAction,
};

EditTokensView.defaultProps = {
  disabledTokens: [],
  baseTokens: [],
};

export default connect(mapStateToProps, mapDispatchToProps)(EditTokensView);

EditTokensView.propTypes = {
  modifiedModalTokens: PropTypes.array.isRequired,
  tokensMap: PropTypes.object.isRequired,
  addedTokens: PropTypes.array.isRequired,
  requiredTokensList: PropTypes.array.isRequired,
  tokensListSearchVal: PropTypes.string.isRequired,
  searchingTokens: PropTypes.bool.isRequired,
  searchingTokensError: PropTypes.string.isRequired,
  tokensSearchResults: PropTypes.array.isRequired,
  chooseTokensAlert: PropTypes.string.isRequired,
  setModifiedModalTokens: PropTypes.func.isRequired,
  createNotificationAction: PropTypes.func.isRequired,
  setCreateHPoolChooseTokensSearchAction: PropTypes.func.isRequired,
  setChooseTicketsAlertAction: PropTypes.func.isRequired,
  onTokenEdit: PropTypes.func.isRequired,
  disabledTokens: PropTypes.array,
  baseTokens: PropTypes.array,
  mostUsedTokens: PropTypes.object.isRequired,
  isVpool: PropTypes.object.isRequired,
};
